import { useMutation, useQueryClient } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import {
  CATALOG_TABLE_ASSETS,
  CRITICAL_ASSETS,
  DEPENDENCIES_COUNTERS,
  DEPENDENCIES_DATA,
  TABLE_ASSETS,
} from '../constants/queryKeys';
import * as api from '../services/api';
import { isInSaaSMode } from '../utils/common';
import useGraphqlQuery from './useGraphqlQuery';
import useEnvPermission from './useEnvPermission';
import useSelectedEnv from './useSelectedEnv';

type SetIsCriticalAssetsMutation = {
  assetsIds: string[];
  isCriticalValue: boolean;
};

export const getCriticalAssetsByIdsQuery = gql`
  query getCriticalAssetsByIds($ids: [String!], $isCritical: Boolean) {
    assets(ids: $ids, isCritical: $isCritical) {
      items {
        id
        isCritical
      }
    }
  }
`;

export type CriticalAssetItem = {
  id: string;
  isCritical: boolean;
};

type Props = {
  ids?: string[];
  isCritical?: boolean;
};

const useCriticalAssets = ({ ids, isCritical }: Props) => {
  const queryClient = useQueryClient();
  const envid = useSelectedEnv();
  const hasPermission = useEnvPermission();

  const hasReadPermissions = hasPermission('VIEW_ASSETS');
  const hasSetPermissions = hasPermission('EDIT_ASSETS') && isInSaaSMode();

  const queryKey = [CRITICAL_ASSETS, envid]
    .concat(ids || [])
    .concat(isCritical != null ? [String(isCritical)] : []);

  const { data, error, isLoading, isFetching } = useGraphqlQuery<{
    assets: { items: CriticalAssetItem[] };
  }>({
    queryKey,
    query: getCriticalAssetsByIdsQuery,
    variables: {
      ids,
      isCritical,
    },
    enabled:
      isInSaaSMode() &&
      (!!ids?.length || isCritical != null) &&
      hasReadPermissions,
  });

  const {
    mutate,
    mutateAsync,
    error: mutationError,
    isLoading: isSetMutationLoading,
  } = useMutation(
    ({ assetsIds, isCriticalValue }: SetIsCriticalAssetsMutation) =>
      api.setIsCriticalAssets({
        ids: assetsIds,
        isCritical: isCriticalValue,
        envId: envid!,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [CRITICAL_ASSETS, envid],
        });
        queryClient.invalidateQueries({
          queryKey: [DEPENDENCIES_COUNTERS, envid],
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: [DEPENDENCIES_DATA, envid],
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: [CATALOG_TABLE_ASSETS, envid],
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: [TABLE_ASSETS, envid],
          exact: false,
        });
      },
    },
  );

  return {
    criticalAssetsData: {
      data: data?.assets.items || [],
      error,
      isLoading,
      isFetching,
    },
    setIsCriticalAssets: {
      setIsCriticalAssetsMutation: mutate,
      setIsCriticalAssetsMutationAsync: mutateAsync,
      error: mutationError,
      isLoading: isSetMutationLoading,
    },
    hasSetPermissions,
  };
};

export default useCriticalAssets;
